import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";

// Global state for tooltip "warm" period - when a tooltip was recently shown,
// subsequent tooltips appear instantly without delay
let lastTooltipCloseTime = 0;
export const TOOLTIP_DELAY = 300;
export const WARM_PERIOD = 500;
const MAX_WIDTH = 300;

export function getLastTooltipCloseTime() {
  return lastTooltipCloseTime;
}
export function setLastTooltipCloseTime(t: number) {
  lastTooltipCloseTime = t;
}

const Container = styled("div")`
  display: inline-flex;
`;

const Trigger = styled("div")`
  display: inline-flex;
  border-radius: 8px;
  cursor: pointer;
`;

const Label = styled("span")`
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0;
`;

const PopoverContent = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  --text-secondary: var(--tooltip-fg-subtle);
  box-shadow: var(--elevation-low);
  padding: 6px 8px;
  z-index: ${zIndexes.tooltip};
  max-width: ${MAX_WIDTH}px;
  width: max-content;
  overflow-wrap: anywhere;
  pointer-events: none;
  text-align: left;
  font-size: 10px;
`;

export type Align = "center" | "left" | "right";
export type Vertical = "above" | "below";

export function getPositionStyles(
  align: Align,
  vertical: Vertical
): React.CSSProperties {
  const styles: Record<string, string> = {};

  if (vertical === "below") {
    styles.top = "anchor(bottom)";
    styles.marginTop = "6px";
  } else {
    styles.bottom = "anchor(top)";
    styles.marginBottom = "6px";
  }

  if (align === "left") {
    styles.left = "anchor(left)";
    styles.translate = "-8px 0";
  } else if (align === "right") {
    styles.right = "anchor(right)";
    styles.translate = "8px 0";
  } else {
    styles.left = "anchor(center)";
    styles.translate = "-50% 0";
  }

  return styles as React.CSSProperties;
}

type Props = {
  content: React.ReactNode;
  children: React.ReactNode;
  align?: Align;
  contentStyle?: React.CSSProperties;
  hidden?: boolean;
  forceOpen?: boolean;
  selectable?: boolean;
  // Make the wrappers stretch and shrink with their parent (rather than
  // sizing to content) so a child can ellipsis-truncate to the available width.
  fill?: boolean;
  // Open to the left of the trigger, vertically centered, instead of the
  // default above/below placement.
  side?: "top" | "left";
};

export function Tooltip({
  content,
  children,
  align: forcedAlign,
  contentStyle,
  hidden,
  forceOpen,
  selectable,
  fill,
  side,
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [align, setAlign] = useState<Align>("center");
  const [vertical, setVertical] = useState<Vertical>("above");
  const triggerRef = useRef<HTMLDivElement>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computePosition = useCallback(() => {
    if (side === "left") return;
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // A centered tooltip needs up to half its max width of room on each side,
    // so anchors within that of an edge align to the edge instead of clipping.
    if (forcedAlign) {
      setAlign(forcedAlign);
    } else if (rect.left < MAX_WIDTH / 2) {
      setAlign("left");
    } else if (viewportWidth - rect.right < MAX_WIDTH / 2) {
      setAlign("right");
    } else {
      setAlign("center");
    }

    setVertical(rect.top < 60 ? "below" : "above");
  }, [forcedAlign, side]);

  const showTooltip = () => {
    computePosition();
    setIsVisible(true);
  };

  const handleMouseEnter = () => {
    if (hidden) return;
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    const isWarm = Date.now() - getLastTooltipCloseTime() < WARM_PERIOD;
    if (isWarm) {
      showTooltip();
    } else {
      delayTimerRef.current = setTimeout(showTooltip, TOOLTIP_DELAY);
    }
  };

  const handleMouseLeave = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (!isVisible) return;
    const hide = () => {
      setLastTooltipCloseTime(Date.now());
      setIsVisible(false);
    };
    if (selectable) delayTimerRef.current = setTimeout(hide, TOOLTIP_DELAY);
    else hide();
  };

  useEffect(() => {
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hidden) {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setIsVisible(false);
    }
  }, [hidden]);

  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    if (forceOpen) computePosition();
  }, [forceOpen, computePosition]);

  const fillStyle: React.CSSProperties | undefined = fill
    ? {
        display: "flex",
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: "auto",
        minWidth: 0,
        maxWidth: "100%",
        cursor: "inherit",
      }
    : undefined;

  const positionStyles: React.CSSProperties =
    side === "left"
      ? { right: "anchor(left)", top: "anchor(center)", translate: "-6px -50%" }
      : getPositionStyles(align, vertical);

  return (
    <Container
      style={fillStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <PopoverContent
        isOpen={isVisible || !!forceOpen}
        style={{
          ...positionStyles,
          ...contentStyle,
          ...(selectable
            ? { pointerEvents: "auto", userSelect: "text" }
            : undefined),
        }}
        trigger={(props) => (
          <Trigger
            ref={triggerRef}
            {...props}
            style={{
              ...fillStyle,
              ...(props as { style?: React.CSSProperties }).style,
            }}
          >
            {children}
          </Trigger>
        )}
      >
        {typeof content === "string" ? <Label>{content}</Label> : content}
      </PopoverContent>
    </Container>
  );
}
